import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { debounce } from "capitalrx-components-2.0";
import { Textbox } from "capitalrx-components-2.0/lib/forms";
import { LoadingGroup, Spinner } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Paragraph, Subheading } from "capitalrx-components-2.0/lib/typography";

import { Drug, useListDrugsQuery } from "@/App/api/drug";
import { NoResults, SectionPanel, ThemedButton } from "@/App/components";
import { useNavigateClient } from "@/App/hooks";

const TRANSLATION_PATH = "FIND_BEST_PRICE.findBestPriceWidget";
const MAX_DRUG_RESULTS_DISPLAYED = 3;
const MIN_SEARCH_LENGTH = 3;

export const FindBestPriceWidget = () => {
  const { t: translate } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get("drugName") || "");
  const [querySearchTerm, setQuerySearchTerm] = useState("");

  const navigate = useNavigateClient();

  const { data: drugs, isLoading: isLoadingDrugs } = useListDrugsQuery({ search: querySearchTerm });

  const { isMobile } = useResponsive({
    isMobile: {
      mobile: true,
      desktop: false,
      tablet: false,
    },
  });

  const onSearchChange = useCallback(
    debounce((value: string) => {
      if (value.length >= MIN_SEARCH_LENGTH) {
        setQuerySearchTerm(value);
      }
    }, 500),
    [],
  );

  useEffect(() => {
    if (searchTerm.length >= MIN_SEARCH_LENGTH) {
      onSearchChange(searchTerm);
    } else {
      onSearchChange("");
    }
  }, [searchTerm]);

  const onDrugSelected = useCallback(
    (drug: Drug) => {
      setSearchParams(`?drugName=${searchTerm}`);
      navigate(`/find-best-price/${drug.id}`);
    },
    [searchTerm],
  );

  const showResults = useMemo(() => !isLoadingDrugs && searchTerm?.length >= MIN_SEARCH_LENGTH && querySearchTerm === searchTerm,
    [querySearchTerm, searchTerm, isLoadingDrugs]);

  const viewAll = useCallback(() => {
    setSearchParams(`?drugName=${searchTerm}`);
    navigate(`/find-best-price?drugName=${searchTerm}`);
  }, [searchTerm]);

  const truncatedDrugsList = useMemo(() => {
    if (drugs?.results) {
      return drugs.results.slice(0, MAX_DRUG_RESULTS_DISPLAYED);
    } else {
      return [];
    }
  }, [drugs]);

  return (
    <Stack spacing="large">
      <Stack>
        <Paragraph>{translate(`${TRANSLATION_PATH}.searchText`)}</Paragraph>
        <Textbox
          hideLabel
          testId="drug-name-search"
          style={{ flexGrow: 1 }}
          value={searchTerm}
          onChange={setSearchTerm}
        />
      </Stack>
      <LoadingGroup loading={searchTerm.length >= MIN_SEARCH_LENGTH && isLoadingDrugs}>
        <LoadingGroup.Placeholder
          fallback={
            <Shelf>
              <Shelf.Spacer />
              <Spinner size="large" />
              <Shelf.Spacer />
            </Shelf>
          }
        >
          {showResults && <Stack>
            {truncatedDrugsList.map((d) => {
              return (
                <SectionPanel key={d.drug.id}>
                  <Shelf alignment="center">
                    <Subheading testId="drug-name">{d.drug.drug_name}</Subheading>
                    <Shelf.Spacer />
                    <ThemedButton
                      testId="view-pricing"
                      onClick={() => onDrugSelected(d.drug)}
                      iconPosition="right"
                      appearance="text"
                      icon="chevron-right"
                    >
                      {translate(`${TRANSLATION_PATH}.selectThisDrug`)}
                    </ThemedButton>
                  </Shelf>
                </SectionPanel>
              );
            })}
          </Stack>}
          {showResults && (drugs?.results || []).length === 0 && (
              <NoResults message={`${translate("FIND_BEST_PRICE.findBestPriceDrugNamePage.noResultsFoundFor")} ${searchTerm}`} />
            )}
          {showResults && drugs?.results?.length > MAX_DRUG_RESULTS_DISPLAYED && (
            <Shelf>
              <ThemedButton appearance="text" onClick={viewAll}>
                {translate(`${TRANSLATION_PATH}.viewAll`)}
              </ThemedButton>
              {!isMobile && <Shelf.Spacer />}
            </Shelf>
          )}
        </LoadingGroup.Placeholder>
      </LoadingGroup>
    </Stack>
  );
};
